import React, { useState, useEffect } from 'react';
import { TablePaginationConfig } from 'antd';
import { useSetRecoilState } from 'recoil';
import { useHistory, useLocation, useParams } from 'react-router';
import { useUpdateEffect } from 'usehooks-ts';
import Skeleton from 'react-loading-skeleton';

import './index.less';

import { getPageFromSearchParams, PAGE_INDEX_PARAM } from '../../../../utils/urlUtil';
import { alertsSelectorAdd } from '../../../../recoil';
import {
  ConnectorModel,
  ELMA365ConnectorSettingsInputTypeEnum,
  ELMA365ConnectorSettingsOutput,
  ELMA365ToolAvailability,
  ELMA365ToolModel,
  ELMA365ToolsetModel,
  ELMA365ToolVisibility,
  ELMA365VersionAction,
} from '../../../../../gamma-api';
import { defaultApi } from '../../../../apis';
import { getToolkitsPath, getToolkitToolPath } from '../../../../utils/pathUtils';
import IbIcon from '../../../../components/common/IbIcon';
import IbTypography from '../../../../components/common/IbTypography';
import IbButton, { IIbButtonAction } from '../../../../components/common/IbButton';
import IbPageTitle, { IBreadcrumb } from '../../../../components/IbPageTitle';
import IbConfirmModal from '../../../../components/common/IbConfirmModal';
import { getTranslateFunc } from '../../../../utils/stringUtil';
import ToolsetModal from '../ToolsetsPage/ToolsetModal';
import VersionMerge from '../ToolsetsPage/VersionMerge';
import ConfirmModal from '../ToolsetsPage/ConfirmModal';
import ImportUpload from '../../../../components/ImportUpload';
import ExportButton from '../../../../components/ExportButton';
import { EMPTY_CONNECTOR } from '../../../../components/ConnectorModal/const';
import { hasObjectId } from '../../../../utils/idUtils';

import {
  CONTENT_CLASS_NAME,
  MAIN_CLASS_NAME,
  SKELETON_CONTAINER_CLASS_NAME,
  SKELETON_HEIGHT,
  SKELETON_WIDTH,
  TOOLBAR_CLASSNAME,
  TOOLS_PAGE_SIZE,
} from './const';
import ToolTable from './ToolTable';
import ToolsetInfoBar from './ToolsetInfoBar';

const ToolsetPage: React.FC = () => {
  const translate = getTranslateFunc('Toolkits: ELMA365: Toolset:');

  const location = useLocation();
  const { push } = useHistory();

  const { folderId, toolsetId } = useParams<{ folderId: string; toolsetId: string }>();

  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const [action, setAction] = useState<ELMA365VersionAction>();
  const [toolset, setToolset] = useState<ELMA365ToolsetModel>();
  const [connector, setConnector] = useState<ConnectorModel>();
  const [toolList, setToolList] = useState<ELMA365ToolModel[]>([]);
  const [deleteTool, setDeleteTool] = useState<ELMA365ToolModel>();

  const [toolsetLoading, setToolsetLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const [toolsetModalVisible, setToolsetModalVisible] = useState(false);
  const [versionMergeVisible, setVersionMergeVisible] = useState(false);
  const [replaceConfirmVisible, setReplaceConfirmVisible] = useState(false);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);

  const [searchText, setSearchText] = useState('');

  const [toolPagination, setToolPagination] = useState<TablePaginationConfig>({
    current: getPageFromSearchParams(location.search),
    pageSize: TOOLS_PAGE_SIZE,
    total: 0,
  });

  const breadcrumbs: IBreadcrumb[] = [{ title: translate('Toolsets'), path: getToolkitsPath(folderId) }];

  const loadToolset = async (): Promise<ELMA365ToolsetModel | undefined> => {
    if (!toolsetId) return;

    try {
      setToolsetLoading(true);

      const toolsetResponse = await defaultApi.getOneELMA365ToolkitToolset(toolsetId);
      const toolset = toolsetResponse.data;
      setToolset(toolset);

      if (hasObjectId(toolset.connector_id)) {
        const connectorResponse = await defaultApi.getOneConnector(toolset.connector_id);
        setConnector(connectorResponse.data);
      } else {
        setConnector(EMPTY_CONNECTOR);
      }

      return toolset;
    } catch (err) {
      addAlert({ content: translate('Failed to load toolset'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
      return undefined;
    } finally {
      setToolsetLoading(false);
    }
  };

  const fetchToolList = async (toolset?: ELMA365ToolsetModel, withLoading?: boolean) => {
    if (!toolset) {
      return;
    }

    try {
      withLoading && setPageLoading(true);
      const response = await defaultApi.searchELMA365ToolkitTools(
        (toolPagination.current || 1) - 1,
        TOOLS_PAGE_SIZE,
        true,
        searchText,
        null,
        null,
        [toolset.version_id],
        null,
        null,
        null,
        [ELMA365ToolVisibility.Visible]
      );
      setToolList(response.data.items || []);
      setToolPagination((prev) => ({ ...prev, total: response.data.total_item_count || 0 }));
    } catch (err) {
      addAlert({ content: translate('Failed to load tools'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      withLoading && setPageLoading(false);
    }
  };

  const onInitialLoadAsync = async () => {
    setListLoading(true);
    const toolset = await loadToolset();
    await fetchToolList(toolset, false);
    setListLoading(false);
  };

  const onInitialLoadSync = () => {
    onInitialLoadAsync().finally();
  };

  useEffect(onInitialLoadSync, [toolsetId]);

  const onToolPageOrSearchChange = async () => {
    await fetchToolList(toolset, true);
  };

  useEffect(() => {
    onToolPageOrSearchChange().finally();
  }, [toolPagination.current, toolPagination.pageSize, searchText]);

  useEffect(() => {
    const page = getPageFromSearchParams(location.search);
    if (page !== toolPagination.current) {
      setToolPagination((prev) => ({ ...prev, current: page }));
    }
  }, [location.search]);

  useUpdateEffect(() => {
    if (toolPagination.current) {
      const params = new URLSearchParams(location.search);
      params.set(PAGE_INDEX_PARAM, toolPagination.current.toString());
      push({ pathname: location.pathname, search: params.toString() });
    }
  }, [toolPagination.current]);

  useUpdateEffect(() => {
    if (toolPagination.current !== 1) {
      setToolPagination({ ...toolPagination, current: 1 });
    }
  }, [searchText]);

  const onToolAvailabilityChange = async (tool: ELMA365ToolModel, availability: ELMA365ToolAvailability) => {
    try {
      await defaultApi.toggleELMA365ToolkitTool(tool.tool_id, {
        visibility: null,
        availability: availability,
      });
    } catch (e) {
      addAlert({
        content: translate('Tool availability change error'),
        type: 'error',
        icon: <IbIcon iconName="attention" />,
      });
    }

    await fetchToolList(toolset, false);
  };

  const onSearchTextChange = (value: string) => setSearchText(value);

  const onToolPaginationChange = (page: number) => setToolPagination({ ...toolPagination, current: page });

  const onAppendButtonClick = () => {
    setAction(ELMA365VersionAction.Appending);
    setToolsetModalVisible(true);
  };

  const onMergeButtonClick = () => {
    setVersionMergeVisible(true);
  };

  const onUpdateButtonClick = () => {
    setAction(undefined);
    setToolsetModalVisible(true);
  };

  const onReplaceButtonClick = () => {
    setReplaceConfirmVisible(true);
  };

  const onImportFileFinish = async (newToolset: ELMA365ToolsetModel) => {
    setToolset(newToolset);
    await fetchToolList(newToolset, true);
  };

  const onToolOpen = (tool: ELMA365ToolModel) => {
    push(getToolkitToolPath(folderId, toolsetId, tool.tool_id));
  };

  const onToolDelete = (tool: ELMA365ToolModel) => {
    setDeleteTool(tool);
  };

  const onToolsetModalFinish = async (toolset: ELMA365ToolsetModel) => {
    setToolsetModalVisible(false);
    setToolset(toolset);
    await fetchToolList(toolset, true);
  };

  const onToolsetModalCancel = () => {
    setToolsetModalVisible(false);
    setAction(undefined);
  };

  const onVersionMergeFinish = () => {
    setVersionMergeVisible(false);
  };

  const onVersionMergeCancel = () => {
    setVersionMergeVisible(false);
  };

  const onReplaceConfirmButtonClick = async () => {
    setAction(ELMA365VersionAction.Creation);
    setReplaceConfirmVisible(false);
    setToolsetModalVisible(true);
  };

  const onReplaceCancelButtonClick = () => {
    setReplaceConfirmVisible(false);
  };

  const onDeleteConfirmButtonClick = async () => {
    if (!deleteTool) {
      return;
    }

    setDeleteConfirmLoading(true);

    try {
      await defaultApi.deleteELMA365ToolkitTool(deleteTool.tool_id);
    } catch (e) {
      addAlert({
        content: translate('Tool deletion error'),
        type: 'error',
        icon: <IbIcon iconName="attention" />,
      });
    }

    await fetchToolList(toolset, false);
    setDeleteConfirmLoading(false);
    setDeleteTool(undefined);
  };

  const onDeleteCancelButtonClick = () => {
    setDeleteTool(undefined);
  };

  const connectorSettings =
    connector?.settings.type === ELMA365ConnectorSettingsInputTypeEnum.Elma365
      ? (connector.settings as ELMA365ConnectorSettingsOutput)
      : undefined;

  const renderToolbar = () => {
    const actions: IIbButtonAction[] = [
      {
        icon: <IbIcon iconName="refresh" />,
        text: translate('Toolbar: Merge'),
        onSelect: onMergeButtonClick,
      },
      {
        icon: <IbIcon iconName="edit" />,
        text: translate('Toolbar: Update'),
        onSelect: onUpdateButtonClick,
      },
      {
        icon: <IbIcon iconName="setting-two" />,
        text: translate('Toolbar: Replace'),
        onSelect: onReplaceButtonClick,
      },
      {
        icon: <IbIcon iconName="efferent-three" />,
        text: translate('Toolbar: Export'),
        render: (child) => <ExportButton.ToolkitExport toolsetId={toolsetId}>{child}</ExportButton.ToolkitExport>,
      },
      {
        icon: <IbIcon iconName="afferent-three" />,
        text: translate('Toolbar: Import'),
        render: (child) => (
          <ImportUpload.ToolkitImport folderId={folderId} toolsetId={toolsetId} onFinish={onImportFileFinish}>
            {child}
          </ImportUpload.ToolkitImport>
        ),
      },
    ];

    return (
      <div className={TOOLBAR_CLASSNAME}>
        <IbButton actions={actions} icon={<IbIcon iconName="add-one" />} onClick={onAppendButtonClick}>
          {translate('Toolbar: Append')}
        </IbButton>
      </div>
    );
  };

  const renderToolTable = () => {
    return (
      <>
        {connector && connectorSettings && (
          <ToolsetInfoBar
            auth={connectorSettings.apis.web.auth}
            name={connector.name}
            url={connectorSettings.apis.web.url}
          />
        )}
        {listLoading && (
          <div className={SKELETON_CONTAINER_CLASS_NAME}>
            <Skeleton height={SKELETON_HEIGHT} width={SKELETON_WIDTH} />
          </div>
        )}
        {!listLoading && renderToolbar()}
        <ToolTable
          dataLoading={listLoading}
          pageLoading={pageLoading}
          pagination={toolPagination}
          searchText={searchText}
          tools={toolList}
          onDelete={onToolDelete}
          onOpen={onToolOpen}
          onPaginationChange={onToolPaginationChange}
          onToolAvailabilityChange={onToolAvailabilityChange}
        />
      </>
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      <IbPageTitle
        showSearchInput
        breadcrumbs={breadcrumbs}
        loading={toolsetLoading}
        title={toolset?.name}
        onDebouncedSearch={onSearchTextChange}
      />
      <div className={CONTENT_CLASS_NAME}>{renderToolTable()}</div>
      <ToolsetModal
        action={action}
        editToolset={toolset}
        visible={toolsetModalVisible}
        onCancel={onToolsetModalCancel}
        onFinish={onToolsetModalFinish}
      />
      <VersionMerge
        toolset={toolset}
        visible={versionMergeVisible}
        onCancel={onVersionMergeCancel}
        onFinish={onVersionMergeFinish}
      />
      <ConfirmModal.ReplaceToolset
        visible={replaceConfirmVisible}
        onCancel={onReplaceCancelButtonClick}
        onConfirm={onReplaceConfirmButtonClick}
      />
      <IbConfirmModal
        footer={
          <>
            <IbButton key="delete-confirm" status="error" onClick={onDeleteConfirmButtonClick}>
              {translate('Delete confirm modal: Delete')}
            </IbButton>
            <IbButton key="delete-cancel" type="secondary" onClick={onDeleteCancelButtonClick}>
              {translate('Delete confirm modal: Cancel')}
            </IbButton>
          </>
        }
        loading={deleteConfirmLoading}
        title={translate('Delete confirm modal: Title')}
        visible={!!deleteTool}
        onCancel={onDeleteCancelButtonClick}
      >
        <IbTypography.Paragraph type="secondary">{translate('Delete confirm modal: Content')}</IbTypography.Paragraph>
      </IbConfirmModal>
    </div>
  );
};

export default ToolsetPage;
